import { useState } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import type { CountryProps } from '../../pages/Cannabis';

interface MusicRec {
  genre: string;
  mood: string;
  artists: string[];
  why: string;
  color: string;
}

const PAIRINGS: Record<string, Record<string, MusicRec>> = {
  energetic: {
    hiphop: { genre: 'Hip-Hop / Rap', mood: 'Energetic', artists: ['Kid Cudi', 'Wiz Khalifa', 'Snoop Dogg', 'Mac Miller', 'Tyler the Creator', 'A$AP Rocky'], why: 'Cannabis and hip-hop have been intertwined since the 90s. The rhythmic flow and bass-heavy beats pair perfectly with an uplifting high.', color: '#f59e0b' },
    electronic: { genre: 'Electronic / House', mood: 'Energetic', artists: ['Flume', 'ODESZA', 'Bonobo', 'Rufus Du Sol', 'Lane 8', 'Tycho'], why: 'Layered electronic production becomes a 3D experience when high. Every synth, beat, and texture pops.', color: '#60a5fa' },
    funk: { genre: 'Funk / Disco', mood: 'Energetic', artists: ['Parliament-Funkadelic', 'Earth Wind & Fire', 'Daft Punk', 'Vulfpeck', 'Jamiroquai', 'Anderson .Paak'], why: 'Groovy basslines and infectious rhythms make funk the ultimate feel-good cannabis pairing.', color: '#f472b6' },
  },
  chill: {
    lofi: { genre: 'Lo-fi / Chillhop', mood: 'Chill', artists: ['Nujabes', 'J Dilla', 'Tomppabeats', 'Idealism', 'Jinsang', 'Aso'], why: 'The genre literally built for smoking sessions. Mellow beats, no lyrics to distract, and an effortlessly cool vibe.', color: '#4ade80' },
    rnb: { genre: 'R&B / Neo-Soul', mood: 'Chill', artists: ['Frank Ocean', 'SZA', 'Daniel Caesar', 'Erykah Badu', 'The Weeknd', 'H.E.R.'], why: 'Smooth vocals over lush production. Cannabis enhances the emotional depth and vocal nuances.', color: '#a78bfa' },
    ambient: { genre: 'Ambient / Space', mood: 'Chill', artists: ['Brian Eno', 'Aphex Twin (ambient)', 'Boards of Canada', 'Tycho', 'Carbon Based Lifeforms', 'Stars of the Lid'], why: 'Ambient music dissolves into the background while creating a profound sense of space and atmosphere. Perfect for introspection.', color: '#06b6d4' },
  },
  trippy: {
    psych: { genre: 'Psychedelic Rock', mood: 'Trippy', artists: ['Pink Floyd', 'Tame Impala', 'King Gizzard', 'Khruangbin', 'Grateful Dead', 'Jefferson Airplane'], why: 'Psychedelic rock was literally made for altered states. Layered guitars, phaser effects, and expansive compositions become transcendent.', color: '#ec4899' },
    world: { genre: 'World / Tribal', mood: 'Trippy', artists: ['Ravi Shankar', 'Ali Farka Toure', 'Tinariwen', 'Bombay Bicycle Club', 'Thievery Corporation', 'Dengue Fever'], why: 'Non-Western instruments and unusual scales sound extraordinary when high. Sitar, oud, kora — each note resonates differently.', color: '#d4a574' },
    experimental: { genre: 'Experimental / IDM', mood: 'Trippy', artists: ['Radiohead', 'Flying Lotus', 'Arca', 'Bjork', 'Four Tet', 'Autechre'], why: 'Complex, unpredictable structures reward the heightened attention that cannabis provides. Every listen reveals new layers.', color: '#8b5cf6' },
  },
  mellow: {
    reggae: { genre: 'Reggae / Dub', mood: 'Mellow', artists: ['Bob Marley', 'Peter Tosh', 'Lee Scratch Perry', 'Steel Pulse', 'Burning Spear', 'Chronixx'], why: 'The original cannabis music. Reggae\'s offbeat rhythm, positive messages, and Rastafarian roots make it the classic pairing.', color: '#4ade80' },
    jazz: { genre: 'Jazz', mood: 'Mellow', artists: ['Miles Davis', 'John Coltrane', 'Chet Baker', 'Kamasi Washington', 'Norah Jones', 'Robert Glasper'], why: 'Jazz musicians have a long history with cannabis. Improvisation and complex harmonies become mesmerizing when high.', color: '#f59e0b' },
    acoustic: { genre: 'Acoustic / Folk', mood: 'Mellow', artists: ['Nick Drake', 'Iron & Wine', 'Bon Iver', 'Fleet Foxes', 'Jose Gonzalez', 'Phoebe Bridgers'], why: 'Stripped-back acoustic music feels intimate and personal. Every string vibration and vocal breath becomes vivid.', color: '#d4a574' },
  },
};

const MOODS = [
  { key: 'energetic', label: 'Energetic', emoji: '', desc: 'Sativa vibes — uplifting, social, active' },
  { key: 'chill', label: 'Chill', emoji: '', desc: 'Hybrid vibes — relaxed but present' },
  { key: 'trippy', label: 'Trippy', emoji: '', desc: 'Cerebral — mind-expanding, contemplative' },
  { key: 'mellow', label: 'Mellow', emoji: '', desc: 'Indica vibes — cozy, sleepy, peaceful' },
];

export default function MusicPairingTab(_props: CountryProps) {
  const [mood, setMood] = useState('chill');
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);

  const genres = PAIRINGS[mood] || PAIRINGS.chill;
  const detail = selectedGenre ? genres[selectedGenre] : null;

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-white">Music Pairing</h2>
        <p className="text-white/40 text-sm">What kind of high are you having? We'll match the music.</p>
      </div>

      {/* Mood selector */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {MOODS.map(m => (
          <button key={m.key} onClick={() => { setMood(m.key); setSelectedGenre(null); }}
            className={`p-3 rounded-xl border text-left transition-colors ${
              mood === m.key ? 'bg-[#4ade80]/10 border-[#4ade80]/30' : 'bg-white/[0.02] border-white/10 hover:border-white/20'
            }`}>
            <p className={`font-semibold text-sm ${mood === m.key ? 'text-[#4ade80]' : 'text-white'}`}>{m.label}</p>
            <p className="text-white/30 text-[10px] mt-0.5">{m.desc}</p>
          </button>
        ))}
      </div>

      {/* Genre cards */}
      <motion.div variants={staggerContainer} initial="initial" animate="animate" key={mood} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(genres).map(([key, rec]) => (
          <motion.button key={key} variants={staggerItem} onClick={() => setSelectedGenre(selectedGenre === key ? null : key)}
            className={`text-left p-4 rounded-2xl border transition-all ${
              selectedGenre === key ? 'border-opacity-40 bg-opacity-10' : 'border-white/10 bg-white/[0.02] hover:border-white/20'
            }`}
            style={selectedGenre === key ? { borderColor: rec.color + '66', backgroundColor: rec.color + '15' } : {}}>
            <p className="font-bold text-sm" style={{ color: selectedGenre === key ? rec.color : 'white' }}>{rec.genre}</p>
            <div className="mt-2 space-y-0.5">
              {rec.artists.slice(0, 3).map(a => (
                <p key={a} className="text-white/40 text-xs">{a}</p>
              ))}
              <p className="text-white/20 text-xs">+{rec.artists.length - 3} more</p>
            </div>
          </motion.button>
        ))}
      </motion.div>

      {/* Detail panel */}
      {detail && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border p-5 space-y-4" style={{ borderColor: detail.color + '33', backgroundColor: detail.color + '08' }}>
          <h3 className="font-bold text-lg" style={{ color: detail.color }}>{detail.genre}</h3>
          <p className="text-white/60 text-sm leading-relaxed">{detail.why}</p>
          <div>
            <p className="text-white/40 text-xs font-semibold mb-2">Recommended Artists</p>
            <div className="flex flex-wrap gap-2">
              {detail.artists.map(a => (
                <span key={a} className="px-2.5 py-1 text-xs rounded-full border" style={{ borderColor: detail.color + '33', color: detail.color + 'cc' }}>{a}</span>
              ))}
            </div>
          </div>
          {/* Streaming links */}
          <div className="space-y-2">
            <p className="text-white/40 text-xs font-semibold">Listen Now</p>
            <div className="flex flex-wrap gap-2">
              {[
                { name: 'Spotify', url: `https://open.spotify.com/search/${encodeURIComponent(detail.artists[0])}`, color: '#1DB954', bg: '#1DB954' },
                { name: 'Apple Music', url: `https://music.apple.com/search?term=${encodeURIComponent(detail.artists[0])}`, color: '#FC3C44', bg: '#FC3C44' },
                { name: 'YouTube Music', url: `https://music.youtube.com/search?q=${encodeURIComponent(detail.artists[0])}`, color: '#FF0000', bg: '#FF0000' },
                { name: 'Anghami', url: `https://play.anghami.com/search/${encodeURIComponent(detail.artists[0])}`, color: '#8236CB', bg: '#8236CB' },
              ].map(svc => (
                <a key={svc.name} href={svc.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors hover:opacity-80"
                  style={{ borderColor: svc.color + '40', color: svc.color, backgroundColor: svc.bg + '15' }}>
                  {svc.name}
                  <span className="text-[10px] opacity-60">&rarr;</span>
                </a>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
